if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.submitButton = this.querySelector('[data-sticky-atc-submit]');
        this.priceTarget = this.querySelector('[data-sticky-atc-price-target]');
        this.quantityInput = this.querySelector(
          '[data-sticky-atc-quantity] .quantity__input'
        );
        this.variantInput = this.querySelector('[data-sticky-atc-variant]');
        this.stickyForm = this.querySelector('.sticky-atc__form');

        this.targetId = this.dataset.targetId;

        this.submitButtonSpinner =
          this.submitButton?.querySelector('.loading__spinner');

        /*
         * Keep the existing sticky Add to Cart behaviour.
         *
         * The sticky button triggers the real PDP Add to Cart button so
         * Dawn's existing product-form/cart-drawer logic continues to work.
         */
        this.submitButton?.addEventListener('click', () => {
          if (!this.realSubmitButton || this.realSubmitButton.disabled) {
            return;
          }

          this.realSubmitButton.click();
        });
      }

      connectedCallback() {
        this.updateOffset();

        this.onResize = () => {
          this.updateOffset();
          this.syncVisibility();
        };

        window.addEventListener('resize', this.onResize);

        this.syncVisibility();

        this.observeMainButton();
        this.observePrice();
        this.observeLoadingState();
        this.observeQuantity();
        this.observeVariantChanges();

        /*
         * Run the initial variant synchronization immediately so the
         * native Shopify payment button starts with the correct variant.
         */
        this.syncVariant();
        this.syncSubmitButtonState();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);

        this.visibilityObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.loadingObserver?.disconnect();
        this.variantObserver?.disconnect();

        this.realQuantityInput?.removeEventListener(
          'change',
          this.onRealQuantityChange
        );

        this.variantInput?.removeEventListener(
          'change',
          this.onStickyVariantChange
        );
      }

      /*
       * ------------------------------------------------------------
       * STICKY POSITION
       * ------------------------------------------------------------
       */

      updateOffset() {
        if (window.matchMedia('(max-width: 989px)').matches) {
          this.style.top = '';
          return;
        }

        const header = document.querySelector('.section-header');

        this.style.top = header
          ? `${header.getBoundingClientRect().height}px`
          : '';
      }

      /*
       * ------------------------------------------------------------
       * MAIN PRODUCT BUTTON
       * ------------------------------------------------------------
       */

      get realSubmitButton() {
        return this.targetId
          ? document.getElementById(this.targetId)
          : null;
      }

      /*
       * ------------------------------------------------------------
       * MAIN QUANTITY INPUT
       * ------------------------------------------------------------
       */

      get realQuantityInput() {
        return this.dataset.sectionId
          ? document.getElementById(`Quantity-${this.dataset.sectionId}`)
          : null;
      }

      /*
       * ------------------------------------------------------------
       * STICKY VISIBILITY
       * ------------------------------------------------------------
       */

      observeMainButton() {
        const target = this.realSubmitButton;

        if (!target || !('IntersectionObserver' in window)) return;

        this.visibilityObserver = new IntersectionObserver(() => {
          this.syncVisibility();
        });

        this.visibilityObserver.observe(target);
      }

      syncVisibility() {
        /*
         * On mobile the sticky ATC is always visible.
         */
        if (window.matchMedia('(max-width: 989px)').matches) {
          this.hidden = false;
          return;
        }

        const target = this.realSubmitButton;

        if (!target) return;

        const scrolledPast =
          target.getBoundingClientRect().bottom < 0;

        if (scrolledPast) {
          this.updateOffset();
        }

        this.hidden = !scrolledPast;
      }

      /*
       * ------------------------------------------------------------
       * PRICE
       * ------------------------------------------------------------
       */

      observePrice() {
        const source = document.getElementById(
          `price-${this.dataset.sectionId}`
        );

        if (
          !source ||
          !this.priceTarget ||
          !('MutationObserver' in window)
        ) {
          return;
        }

        this.priceObserver = new MutationObserver(() => {
          this.priceTarget.innerHTML = source.innerHTML;
        });

        this.priceObserver.observe(source, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      /*
       * ------------------------------------------------------------
       * LOADING STATE
       * ------------------------------------------------------------
       */

      observeLoadingState() {
        const target = this.realSubmitButton;

        if (
          !target ||
          !this.submitButton ||
          !('MutationObserver' in window)
        ) {
          return;
        }

        const sync = () => {
          const isLoading = target.classList.contains('loading');

          this.submitButton.classList.toggle(
            'loading',
            isLoading
          );

          this.submitButtonSpinner?.classList.toggle(
            'hidden',
            !isLoading
          );
        };

        this.loadingObserver = new MutationObserver(sync);

        this.loadingObserver.observe(target, {
          attributes: true,
          attributeFilter: ['class'],
        });

        /*
         * Run once initially.
         */
        sync();
      }

      /*
       * ------------------------------------------------------------
       * QUANTITY
       * ------------------------------------------------------------
       */

      observeQuantity() {
        const realInput = this.realQuantityInput;

        if (!this.quantityInput || !realInput) return;

        this.onRealQuantityChange = () => {
          if (this.quantityInput.value !== realInput.value) {
            this.quantityInput.value = realInput.value;
          }
        };

        /*
         * When the customer changes quantity from the sticky ATC,
         * keep the main PDP quantity synchronized.
         */
        this.quantityInput.addEventListener('change', () => {
          if (realInput.value === this.quantityInput.value) {
            return;
          }

          realInput.value = this.quantityInput.value;

          realInput.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        });

        /*
         * When the main PDP quantity changes, update the sticky quantity.
         */
        realInput.addEventListener(
          'change',
          this.onRealQuantityChange
        );

        this.onRealQuantityChange();
      }

      /*
       * ------------------------------------------------------------
       * VARIANT
       * ------------------------------------------------------------
       *
       * Dawn updates the main product form's hidden variant input
       * when the selected variant changes.
       *
       * We copy that value to the sticky form's hidden:
       *
       * <input name="id" data-sticky-atc-variant>
       *
       * This is important because Shopify's native accelerated
       * checkout button needs the current variant.
       */

      observeVariantChanges() {
        const sectionId = this.dataset.sectionId;

        const hiddenInput = sectionId
          ? document.querySelector(
              `#product-form-${sectionId} .product-variant-id`
            )
          : null;

        if (!hiddenInput || !this.variantInput) return;

        this.mainVariantInput = hiddenInput;

        /*
         * Dawn normally dispatches a change event when it updates
         * the selected variant.
         */
        this.onMainVariantChange = () => {
          this.syncVariant();
          this.syncSubmitButtonState();
        };

        hiddenInput.addEventListener(
          'change',
          this.onMainVariantChange
        );

        hiddenInput.addEventListener(
          'input',
          this.onMainVariantChange
        );

        /*
         * Additional MutationObserver fallback.
         *
         * Some custom variant implementations update the value
         * attribute directly instead of dispatching change/input.
         */
        if ('MutationObserver' in window) {
          this.variantObserver = new MutationObserver(() => {
            this.syncVariant();
            this.syncSubmitButtonState();
          });

          this.variantObserver.observe(hiddenInput, {
            attributes: true,
            attributeFilter: ['value'],
          });
        }

        /*
         * Also listen for Dawn's product-info variant event if
         * available in the theme.
         */
        this.onVariantChangeEvent = () => {
          this.syncVariant();
          this.syncSubmitButtonState();
        };

        this.addEventListener(
          'variant-change',
          this.onVariantChangeEvent
        );
      }

      syncVariant() {
        if (!this.variantInput) return;

        let variantId = '';

        /*
         * Get the currently selected variant from the main PDP form.
         */
        if (this.mainVariantInput) {
          variantId = this.mainVariantInput.value;
        }

        /*
         * Fallback to the data attribute if the main input is not
         * available yet.
         */
        if (!variantId) {
          variantId = this.dataset.variantId || '';
        }

        if (!variantId) return;

        /*
         * Update the sticky Shopify product form.
         */
        if (this.variantInput.value !== variantId) {
          this.variantInput.value = variantId;

          /*
           * Notify Shopify's form/payment-button logic that the
           * product variant has changed.
           */
          this.variantInput.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        }

        /*
         * Keep the component's data attribute synchronized too.
         */
        this.dataset.variantId = variantId;
      }

      /*
       * ------------------------------------------------------------
       * STICKY ADD TO CART AVAILABILITY
       * ------------------------------------------------------------
       */

      syncSubmitButtonState() {
        const realSubmitButton = this.realSubmitButton;

        if (!this.submitButton || !realSubmitButton) return;

        /*
         * Keep sticky Add to Cart disabled/enabled state
         * synchronized with the main PDP button.
         */
        this.submitButton.disabled = realSubmitButton.disabled;

        /*
         * Keep the hidden variant ID synchronized as well.
         */
        this.syncVariant();
      }
    }
  );
}